import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperclip, FaPaperPlane } from 'react-icons/fa6';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS, U3204ROLES } from '../U3204';
import { useU3204Context } from '../U3204Context';
import U3204ChatComposerFooter from './U3204ChatComposerFooter';
import U3204QuotaUsageFooter from './U3204QuotaUsageFooter';
import { useU3204SendMessageHandlers } from '../hooks/useU3204SendMessageHandlers';
import { useU3204QuotaStatus } from '../hooks/useU3204QuotaStatus';
import { u3204BuildAcceptAttribute } from '../hooks/useU3204ModelCapabilitiesHandlers';
import { u3204FormatFileSize } from '../utils/u3204AttachmentUtils';
import { u3204DescribeSupportedAttachmentFormatKinds } from '../utils/u3204DescribeSupportedAttachmentFormatKinds';

const U3204ChatInput: React.FC = () => {
  const { t } = useTranslation(NS);
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const { activeConversationId, streamState, allowedAttachmentMimeTypes, canAttachFiles, canSendInActiveConversation } =
    useU3204Context();
  const { quotaStatus, visible: quotaVisible, blocked: quotaBlocked, blockReason, refreshQuotaStatus } =
    useU3204QuotaStatus();
  const { sendMessage } = useU3204SendMessageHandlers({
    onAfterQuotaError: () => { void refreshQuotaStatus(); },
  });
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [quotaExpanded, setQuotaExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptAttribute = useMemo(
    () => u3204BuildAcceptAttribute(allowedAttachmentMimeTypes),
    [allowedAttachmentMimeTypes],
  );

  const attachButtonTitle = useMemo(() => {
    const kinds = u3204DescribeSupportedAttachmentFormatKinds(allowedAttachmentMimeTypes);
    if (kinds.length === 0) {
      return t('attachFile', { ns: NS });
    }
    const formatLabels = kinds.map((kind) => t(`attachFormat_${kind}`, { ns: NS }));
    return t('attachSupportedFormats', { ns: NS, formats: formatLabels.join(', ') });
  }, [allowedAttachmentMimeTypes, t]);

  const quotaBlockedTooltip = useMemo(() => {
    if (!quotaBlocked) return undefined;
    return blockReason === 'group'
      ? t('quota.blockedGroup', { ns: NS })
      : t('quota.blockedUser', { ns: NS });
  }, [blockReason, quotaBlocked, t]);

  const canAdd = hasRole(U3204ROLES.ADD);
  const isStreaming = streamState === 'streaming';
  const isConversationReadOnly = Boolean(activeConversationId) && !canSendInActiveConversation;
  const disabled = !canAdd || !activeConversationId || isStreaming || !canSendInActiveConversation || quotaBlocked;

  const handleSend = useCallback(async () => {
    if (disabled) return;
    const text = content;
    const attached = [...files];
    setContent('');
    setFiles([]);
    await sendMessage(text, attached);
  }, [content, disabled, files, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const list = Array.from(incoming);
      if (list.length === 0) return;

      const rejected = list.filter((file) => !allowedAttachmentMimeTypes.includes(file.type));
      if (rejected.length > 0) {
        toast.error(t('attachMimeRejected', { ns: NS }));
      }

      const accepted = list.filter((file) => allowedAttachmentMimeTypes.includes(file.type));
      if (accepted.length === 0) return;
      setFiles((prev) => [...prev, ...accepted]);
    },
    [allowedAttachmentMimeTypes, t, toast],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) addFiles(e.target.files);
      e.target.value = '';
    },
    [addFiles],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled || !canAttachFiles) return;
      if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
    },
    [addFiles, canAttachFiles, disabled],
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const sendButtonTitle = quotaBlockedTooltip ?? t('send', { ns: NS });

  return (
    <div
      className={`shrink-0 border-t border-border bg-surface p-4 ${isDragOver ? 'ring-2 ring-primary ring-inset' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled && canAttachFiles) setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      {isConversationReadOnly ? (
        <p className="mb-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          {t('conversationReadOnly', { ns: NS })}
        </p>
      ) : null}

      {files.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-2">
          {files.map((file, index) => (
            <span
              key={`${file.name}-${index}`}
              className="inline-flex items-center gap-2 rounded-lg bg-muted px-2 py-1 text-xs"
            >
              <span>{file.name}</span>
              <span className="text-muted-foreground">({u3204FormatFileSize(file.size)})</span>
              <button type="button" onClick={() => removeFile(index)} className="text-muted-foreground hover:text-foreground">
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileChange}
          accept={acceptAttribute}
        />
        {canAttachFiles ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted disabled:opacity-50"
            title={attachButtonTitle}
          >
            <FaPaperclip />
          </button>
        ) : null}

        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
          placeholder={t('inputPlaceholder', { ns: NS })}
          title={quotaBlockedTooltip}
          className={`min-h-10 max-h-48 min-w-0 flex-1 resize-y rounded-lg border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-hidden focus:ring-2 focus:ring-primary/20 disabled:opacity-50 ${
            quotaBlocked ? 'border-red-300' : 'border-border'
          }`}
        />

        <button
          type="button"
          disabled={disabled || (!content.trim() && files.length === 0)}
          onClick={() => void handleSend()}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
          title={sendButtonTitle}
        >
          <FaPaperPlane />
        </button>
      </div>
      {quotaVisible && quotaStatus ? (
        <div className="mt-2 flex gap-2">
          {canAttachFiles ? <div className="w-10 shrink-0" aria-hidden="true" /> : null}
          <U3204QuotaUsageFooter
            quotaStatus={quotaStatus}
            expanded={quotaExpanded}
            onExpandedChange={setQuotaExpanded}
          />
          <div className="w-10 shrink-0" aria-hidden="true" />
        </div>
      ) : null}
      <U3204ChatComposerFooter hideMissionModel={quotaVisible} hideTokenSummary={quotaVisible} />
    </div>
  );
};

export default U3204ChatInput;
